// Catalog.ID Client — `create-mail-address`: the mail-address record ceremony
// ============================================================================
// The only writer of the `mail_address` claim (migration §5.3). The mail
// backend supplies the VALUE — it is the side that knows the home operator code
// and the account's published `mail_encrypt` key — and this tab does the
// crypto, because claim values are client-encrypted before they ever reach the
// CID server.
//
// The authoritative contract is the XML doc on
// `CrossModuleCanonical.BuildCreateMailAddress`
// (apps/desktop/src/shared/moduleipc/CrossModuleCanonical.cs). Body fields:
// `mailAddressValue`, `mailAddressDigestBase64`, `requiresUserInteraction:
// true`. Approved shape: `{status:"approved", claimUuid}`.
//
// WHAT THIS MODE DOES NOT DO: it does not PUBLISH, and it does not register
// with the mail directory. The canonical digest commits to the VALUE ALONE, so
// the mode may do exactly what the value determines — create the record.
// Publication is what turns it into a directory-visible public identifier, and
// directory registration additionally needs an operator-side allocation check
// (gateway-api §6: "a published claim never creates an address"). Driving
// either from an un-committed request field would be a privilege the
// module-identity signature never covered.
//
// LOCK POSTURE. Ceremony-grade AND consent-gated, like chat's
// `create-chat-address`: on a LOCKED bag it UNLOCKS FIRST and asks second
// (M2c reversed the old order, 2026-09-18). Asking first used to spare a
// declining member an unlock they did not need — nothing in the dialog is
// derived from the keybag — but a dialog rendered in a tab the member is not
// looking at is invisible, and the focus-request the unlock gate sends is what
// raises this tab. On an unlocked bag the gate is a no-op and the dialog still
// precedes every keybag read.
//
// Gate stack (in order):
//   1–3. sender stamp / requester / active identity — `dispatchMailRequest`.
//   4.   required fields present.
//   5.   body↔canonical binding: SHA-512(utf8(value)) equals the signed
//        `mailAddressDigestBase64`. The canonical arm committed to that digest
//        and the value rides in the body, so without this a relay-verified
//        envelope could have another address — or another mail_encrypt key —
//        substituted under it.
//   6.   the value is a `mail_address.v1` record over the ACTIVE identity, with
//        the §5.3 closed field set and a well-formed address.
//   7.   UNLOCK — the universal redirect (M2c): a locked bag raises this tab
//        and waits out the shared budget, whatever the sender's flag says.
//   8.   CONSENT — a modal naming the address. Declining is a distinct refusal.
//   9.   `attr_encrypt` is in the bag (the claim key is wrapped to it).
//  10.   create-or-reuse the claim (`mail-address-store.ts`, idempotent).

import { sha512 } from '@noble/hashes/sha2.js';
import { keys } from '../state/keys';
import * as aes from '../crypto/aes';
import { ATTR_ENCRYPT_KEY_ID, ClaimShareError } from '../state/claim-sharing';
import type { MailRequest, MailResponse, MailDispatchDeps } from './mail-types';
import { ensureUnlocked } from './unlock-gate';

/** Migration §5.3: the record type this mode accepts, and only this one. */
export const MAIL_ADDRESS_RECORD_TYPE = 'mail_address.v1';

/** §5.3's closed field set. An unknown field means a newer record type, not a v1. */
const MAIL_ADDRESS_FIELDS = new Set([
    'type', 'address', 'cid', 'mailEncryptPubkey', 'operatorId', 'updatedAt',
]);

/** The combined `mail_encrypt` (X25519 + ML-KEM-1024) public key is 1600 bytes
 *  — the same shape as `chat_encrypt` and `efs_encrypt` (envelope-wire §3). */
export const MAIL_ENCRYPT_PUBLIC_KEY_LEN = 1600;

/**
 * A single RFC 5321 mailbox: exactly one `@`, a non-empty local part with no
 * whitespace, and a dotted domain. Deliberately conservative rather than a full
 * RFC 5322 grammar — this is a gate against a value that is not an address at
 * all (a list, a display name, an empty string), not an attempt to out-parse a
 * mail server. Anything exotic and legitimate that this rejects is a
 * conversation, not a silent acceptance.
 */
const ADDRESS_PATTERN = /^[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>".]+$/;

type ResponseBase = Pick<MailResponse, 'type' | 'to' | 'requestId'>;

/**
 * Digest convention shared with the mail service's canonical builder and the
 * relay's C# mirror (`CrossModuleCanonical.ComputeMailAddressDigest`): base64
 * SHA-512 over the UTF-8 bytes of the JCS-canonical value.
 *
 * ONE digest over the VALUE ALONE — deliberately NOT chat's two-part
 * `peerCid + "|" + value`. Chat needed a second committed half because the peer
 * being consented to sits OUTSIDE the value; mail has no peer half at all, and
 * its closed value set already binds every part of the act (which address,
 * whose account, which key routes it, which operator).
 */
export function computeMailAddressDigest(mailAddressValue: string): string {
    return aes.toBase64(sha512(new TextEncoder().encode(mailAddressValue)));
}

/** The parsed `mail_address.v1` record, or a stable refusal code. */
export type MailAddressParse =
    | { ok: true; record: MailAddressRecord }
    | { ok: false; reason: string };

export interface MailAddressRecord {
    type: string;
    address: string;
    cid: string;
    mailEncryptPubkey: string;
    operatorId: string;
    updatedAt: string;
}

/**
 * Validate the claim VALUE against migration §5.3 before anything durable
 * happens.
 *
 * Three of these checks are load-bearing rather than hygiene:
 *
 *   * `cid === accountCid` — the requester does not get to name the account its
 *     record is written under. Without it, a mail module talking to the wrong
 *     tab would record someone else's address under this account, and the
 *     consent dialog would truthfully say "recording under <you>" about a
 *     record that is not yours. Same posture as `issue-mail-cert` binding the
 *     cert to the ACTIVE identity.
 *   * `mailEncryptPubkey` length — this key is what inbound mail to the address
 *     gets encrypted to (gateway-api §8). A substituted or truncated key means
 *     mail the account cannot read, or worse, mail someone else can.
 *   * unknown fields refused — §5.3 calls the field set closed, and the version
 *     lives in `type`. A v2 record must arrive as `mail_address.v2` and be
 *     handled deliberately, not smuggled past a v1 handler that ignores what it
 *     does not recognise.
 */
export function parseMailAddressValue(
    mailAddressValue: string, accountCid: string,
): MailAddressParse {
    let parsed: unknown;
    try {
        parsed = JSON.parse(mailAddressValue);
    } catch {
        return { ok: false, reason: 'mail-address-not-json' };
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return { ok: false, reason: 'mail-address-not-object' };
    }

    const obj = parsed as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
        if (!MAIL_ADDRESS_FIELDS.has(key)) {
            return { ok: false, reason: 'mail-address-unknown-field' };
        }
    }
    for (const key of MAIL_ADDRESS_FIELDS) {
        if (typeof obj[key] !== 'string' || (obj[key] as string).length === 0) {
            return { ok: false, reason: 'mail-address-missing-field' };
        }
    }

    const record = obj as unknown as MailAddressRecord;
    if (record.type !== MAIL_ADDRESS_RECORD_TYPE) {
        return { ok: false, reason: 'mail-address-wrong-type' };
    }
    if (record.cid !== accountCid) {
        return { ok: false, reason: 'mail-address-not-own-cid' };
    }
    if (!ADDRESS_PATTERN.test(record.address)) {
        return { ok: false, reason: 'mail-address-malformed-address' };
    }

    let pubkey: Uint8Array;
    try {
        pubkey = aes.fromBase64(record.mailEncryptPubkey);
    } catch {
        return { ok: false, reason: 'mail-address-bad-pubkey-base64' };
    }
    if (pubkey.length !== MAIL_ENCRYPT_PUBLIC_KEY_LEN) {
        return { ok: false, reason: 'mail-address-pubkey-wrong-length' };
    }

    // The home operator CODE — never a URL. A URL here would be silently stored
    // and later handed to a client as an operator id (the chat trap, adopted).
    if (/[:/\\]/.test(record.operatorId)) {
        return { ok: false, reason: 'mail-address-operator-not-a-code' };
    }

    return { ok: true, record };
}

function unavailable(base: ResponseBase, reason: string): MailResponse {
    return { ...base, status: 'unavailable', reason };
}

export async function handleCreateMailAddress(
    request: MailRequest,
    base: ResponseBase,
    accountCid: string,
    deps: MailDispatchDeps,
): Promise<MailResponse> {
    // Gate 4.
    if (!request.mailAddressValue || !request.mailAddressDigestBase64) {
        return unavailable(base, 'missing-required-field');
    }

    // Gate 5: body↔canonical binding.
    if (computeMailAddressDigest(request.mailAddressValue)
        !== request.mailAddressDigestBase64) {
        return unavailable(base, 'mail-address-digest-mismatch');
    }

    // Gate 6.
    const parsed = parseMailAddressValue(request.mailAddressValue, accountCid);
    if (!parsed.ok) return unavailable(base, parsed.reason);

    // Gate 7: the universal unlock redirect (M2c, §10 item 5), AHEAD of the
    // consent dialog — a dialog in an unfocused tab is invisible, and the
    // focus-request is what makes this tab visible. A no-op on an open bag.
    if (!await ensureUnlocked(request, deps)) return unavailable(base, 'cid-locked');

    // Gate 8: consent. A durable record naming a public identifier is never
    // written implicitly.
    if (!deps.confirmMailAddressCreate) {
        // Wired in by `key-grant/client.ts`. Absent means a build that cannot
        // ask, and a mode that writes durable account state must never proceed
        // without asking.
        return unavailable(base, 'consent-ui-unavailable');
    }
    const decision = await deps.confirmMailAddressCreate({
        owningCid: accountCid,
        address: parsed.record.address,
        requesterId: request.requesterId,
        operatorId: parsed.record.operatorId,
        mailEncryptFingerprint: toHexLower(sha512(aes.fromBase64(parsed.record.mailEncryptPubkey))),
    });
    if (decision !== 'approve') {
        return { ...base, status: 'denied', reason: 'mail-address-declined' };
    }

    // Gate 9. Every claim symmetric key is wrapped to this slot; without it
    // neither the reuse scan nor the fresh wrap can run.
    if (!keys.getPrivateKey(ATTR_ENCRYPT_KEY_ID)) {
        return unavailable(base, 'attr-encrypt-not-in-bag');
    }

    // Gate 10.
    if (!deps.createMailAddressClaim) {
        return unavailable(base, 'claim-store-unavailable');
    }
    try {
        const result = await deps.createMailAddressClaim({
            mailAddressValue: request.mailAddressValue,
            label: `Mail address ${parsed.record.address}`,
        });
        return {
            ...base,
            status: 'approved',
            // Server-assigned, echoed — never predicted (ownership D1).
            claimUuid: result.claimUuid,
        };
    } catch (err) {
        // `ClaimShareError` codes are stable and mail branches on them; anything
        // else is reported by name so a transport failure is not mistaken for a
        // refusal.
        if (err instanceof ClaimShareError) return unavailable(base, err.code);
        const message = err instanceof Error ? err.message.slice(0, 80) : 'mail-address-failed';
        return unavailable(base, message);
    }
}

function toHexLower(bytes: Uint8Array): string {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
        const b = bytes[i] as number;
        out += (b < 16 ? '0' : '') + b.toString(16);
    }
    return out;
}
